import { CONTROL_DOMAINS } from './controls.js';
import { evaluate, isAnswered } from './conditions.js';
import { belowMinimum } from './profiles.js';
import { displayStates, withImplied } from './questions.js';
/**
 * The CyberScore engine (model v4.2): a 0–1000 rating built in four layers.
 *
 *  1. Inherent risk (0–100): exposure before any controls (industry, size, technology).
 *  2. Control effectiveness (0–100): the 12 domains, each control weighted by criticality, credited by how
 *     far it is in place (the `score_change` effects of answers and rules).
 *  3. Evidence confidence: how sure we are each answer is true, from where it came from.
 *  4. Threats and losses: resilience per threat, and the expected annual loss.
 *
 *     effective controls  CE = declared × (0.75 + 0.25 × evidence confidence)
 *     residual risk       RR = inherent × (1 − 0.8 × CE / 100)
 *     CyberScore             = 1000 − 10.5 × RR, then capped by any failed kill switch
 *
 * Pure and deterministic: the same library, answers, facts and effects always give the same result, and
 * the algorithm id names the version of every constant below. Changing a constant is a new version.
 */
export const CYBERSCORE_ALGORITHM = 'cyberscore/v4.2';
// ---------- model v4.2 constants ----------
export const SCORE_MIN = 300;
export const SCORE_MAX = 1000;
export const SCORE_BANDS = [
    { key: 'POOR', min: 300, max: 549 },
    { key: 'FAIR', min: 550, max: 649 },
    { key: 'GOOD', min: 650, max: 749 },
    { key: 'VERY_GOOD', min: 750, max: 849 },
    { key: 'EXCELLENT', min: 850, max: 1000 },
];
export const INDUSTRY_MULTIPLIERS = {
    manufacturing: 1.25,
    healthcare: 1.45,
    retail_ecommerce: 1.2,
    professional_services: 1.1,
    logistics: 1.2,
    financial_services: 1.5,
    energy: 1.5,
    utilities: 1.4,
    telecoms: 1.35,
    government: 1.4,
    education: 1.15,
};
/** What failing a kill switch caps the score at, and how serious it is for underwriting. */
export const KILL_SWITCH_CAPS = {
    'NS-01': { cap: 549, severity: 'critical' },
    'VM-02': { cap: 549, severity: 'critical' },
    'IA-01': { cap: 599, severity: 'critical' },
    'IA-02': { cap: 599, severity: 'critical' },
    'IA-03': { cap: 599, severity: 'critical' },
    'AM-03': { cap: 599, severity: 'critical' },
    'BR-02': { cap: 649, severity: 'high' },
    'EP-01': { cap: 649, severity: 'high' },
    'BR-03': { cap: 679, severity: 'high' },
    'EH-03': { cap: 679, severity: 'high' },
    'TP-02': { cap: 679, severity: 'high' },
    'IR-01': { cap: 699, severity: 'medium' },
};
/** A kill switch added to the library later, before the model gives it its own cap. */
export const DEFAULT_KILL_SWITCH_CAP = { cap: 649, severity: 'high' };
/** What failing a control caps the score at, if it is a kill switch: the industry profile's cap first. */
export function killSwitchCap(control, def, profile) {
    const own = profile?.kill_switches.find((k) => k.control === control);
    if (own)
        return { cap: own.cap, severity: own.severity };
    return def?.kill_switch ? (KILL_SWITCH_CAPS[control] ?? DEFAULT_KILL_SWITCH_CAP) : null;
}
/** How far an answer can be trusted, by where it came from (the evidence ladder). */
export const EVIDENCE_CONFIDENCE = {
    USER_DECLARED: 0.2,
    ADMIN_ENTERED: 0.2,
    INFERRED: 0.2,
    AI_EXTRACTED: 0.4,
    DOCUMENT_VERIFIED: 0.6,
    SECURITY_SCAN: 0.75,
    EXTERNAL_PROVIDER: 0.75,
    API_DISCOVERED: 0.9,
};
/** The answers inherent risk reads (questions of the platform journeys). */
export const INHERENT_INPUTS = {
    revenue: 'Q301',
    staff: 'Q012',
    ot: 'Q031',
    own_servers: 'P-SERVERS',
    remote_access: 'P-REMOTE',
    website: 'P-WEBSITE',
    cloud_iaas: 'P-IAAS',
    cloud_email: 'Q021',
};
const REVENUE_POINTS = [
    [1_000_000, 4],
    [5_000_000, 8],
    [20_000_000, 12],
    [50_000_000, 16],
    [100_000_000, 20],
    [Infinity, 24],
];
/** Personal records held (customers, patients, staff): the data volume a breach would expose. */
const RECORD_POINTS = [
    [1_000, 0],
    [10_000, 2],
    [50_000, 4],
    [100_000, 6],
    [Infinity, 8],
];
const STAFF_POINTS = [
    [20, 3],
    [50, 6],
    [100, 9],
    [250, 12],
    [Infinity, 15],
];
/** Which domains protect against each threat, and how much. Weights per threat sum to 1. */
export const THREATS = {
    ransomware: { name: 'Ransomware', domains: { BR: 0.35, EP: 0.2, VM: 0.15, IA: 0.15, IR: 0.15 } },
    breach: { name: 'Data breach', domains: { DP: 0.35, IA: 0.25, LD: 0.15, VM: 0.15, EP: 0.1 } },
    bec: { name: 'Email fraud (BEC)', domains: { EH: 0.5, IA: 0.3, GV: 0.2 } },
    cloud: { name: 'Cloud / SaaS outage', domains: { IR: 0.4, TP: 0.3, BR: 0.3 } },
    third: { name: 'Supplier cyber event', domains: { TP: 0.6, IR: 0.2, NS: 0.2 } },
};
/** Annual frequency per threat and severity drivers, by industry (reference figures, before calibration). */
const LOSS_BASE = {
    manufacturing: { ransomware: 0.11, breach: 0.04, bec: 0.14, cloud: 0.07, third: 0.05, per_record: 18, downtime: 1.6 },
    healthcare: { ransomware: 0.12, breach: 0.09, bec: 0.1, cloud: 0.06, third: 0.05, per_record: 42, downtime: 1.3 },
    logistics: { ransomware: 0.1, breach: 0.04, bec: 0.13, cloud: 0.08, third: 0.07, per_record: 16, downtime: 1.5 },
    professional_services: { ransomware: 0.07, breach: 0.06, bec: 0.21, cloud: 0.05, third: 0.04, per_record: 30, downtime: 0.9 },
    retail_ecommerce: { ransomware: 0.08, breach: 0.09, bec: 0.11, cloud: 0.07, third: 0.05, per_record: 24, downtime: 1.2 },
    financial_services: { ransomware: 0.06, breach: 0.07, bec: 0.12, cloud: 0.06, third: 0.06, per_record: 55, downtime: 1.2 },
    // Task 81: OT-heavy sectors lose most to downtime; schools are hit by ransomware most and hold cheap records.
    energy: { ransomware: 0.13, breach: 0.03, bec: 0.12, cloud: 0.05, third: 0.07, per_record: 20, downtime: 2 },
    utilities: { ransomware: 0.11, breach: 0.05, bec: 0.1, cloud: 0.05, third: 0.06, per_record: 15, downtime: 1.8 },
    telecoms: { ransomware: 0.08, breach: 0.08, bec: 0.1, cloud: 0.07, third: 0.07, per_record: 22, downtime: 1.6 },
    government: { ransomware: 0.12, breach: 0.07, bec: 0.13, cloud: 0.05, third: 0.05, per_record: 20, downtime: 1.1 },
    education: { ransomware: 0.14, breach: 0.07, bec: 0.12, cloud: 0.05, third: 0.04, per_record: 12, downtime: 0.8 },
    other: { ransomware: 0.09, breach: 0.06, bec: 0.14, cloud: 0.06, third: 0.05, per_record: 25, downtime: 1.2 },
}; // prettier-ignore
/** Without a records question, records held are estimated from headcount. */
const RECORDS_PER_STAFF = 100;
/** The provenance an answer counts with: raised to AI_EXTRACTED when its claim checks support it. */
export function checkedProvenance(provenance, verdict) {
    return verdict === 'supported' && EVIDENCE_CONFIDENCE[provenance] < EVIDENCE_CONFIDENCE.AI_EXTRACTED
        ? 'AI_EXTRACTED'
        : provenance;
}
// ---------- the engine ----------
const round = (n, d = 1) => Math.round(n * 10 ** d) / 10 ** d;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const byKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
export function bandOf(score) {
    return (SCORE_BANDS.find((b) => score >= b.min && score <= b.max) ?? SCORE_BANDS[0]).key;
}
/** Layers 2–3 combined with layer 1 into the score, before caps. The model's formula, in one place. */
export function combineLayers(inherent, declared, evidenceConfidence) {
    const effective = declared * (0.75 + 0.25 * evidenceConfidence);
    const residual = inherent * (1 - (0.8 * effective) / 100);
    const score = clamp(Math.round(1000 - 10.5 * residual), SCORE_MIN, SCORE_MAX);
    return { effective, residual, score };
}
/**
 * Layers 2–3 from the controls, combined with layer 1, then capped by failed kill switches. Sums run in
 * control-id order, so the same controls give exactly the same score however they are listed.
 */
export function scoreBasis(b) {
    let credit = 0;
    let confident = 0;
    let cap = null;
    for (const c of [...b.controls].sort((x, y) => byKey(x.control, y.control))) {
        const impl = c.implementation ?? 0;
        credit += c.weight * impl;
        confident += c.weight * impl * c.confidence;
        if (c.cap !== null && c.implementation === 0)
            cap = cap === null ? c.cap : Math.min(cap, c.cap);
    }
    const declared = 100 * credit;
    const evidence = credit ? confident / credit : 0;
    const layers = combineLayers(b.inherent, declared, evidence);
    return {
        declared,
        evidence,
        effective: layers.effective,
        residual: layers.residual,
        uncapped: layers.score,
        cap,
        score: cap === null ? layers.score : Math.min(layers.score, cap),
    };
}
export function computeCyberScore(input) {
    return runCyberScore(input).result;
}
/**
 * The engine, with what explanations need besides the result: the exact basis, the questions that ask
 * about each applicable control, and the effects that credit it.
 */
export function runCyberScore(given) {
    const values = {};
    for (const [k, a] of Object.entries(given.answers))
        values[k] = a.value;
    const states = displayStates(given.questions, { answers: values, facts: given.facts });
    // A question a route counts as not in place (task 85) is answered with that, inferred.
    const input = { ...given, answers: withImplied(given.answers, states, (value) => ({ value, provenance: 'INFERRED' })) }; // prettier-ignore
    const out = new Set(input.out_of_scope ?? []);
    const shown = (k) => states[k] !== undefined && states[k].state !== 'hidden' && !out.has(k);
    const answered = (k) => shown(k) && isAnswered(input.answers[k]?.value);
    // A control applies when a question shown in this assessment can credit it (a profile question that only
    // mentions a control, such as "are machines connected?", doesn't make it apply).
    const askedBy = new Map();
    for (const [key, q] of Object.entries(input.questions)) {
        if (!shown(key))
            continue;
        for (const c of scoredControls(q))
            if (input.controls[c])
                askedBy.set(c, [...(askedBy.get(c) ?? []), key]);
    }
    // How far each control is in place: the lowest credit any current effect gives it.
    const implementation = new Map();
    const credits = new Map();
    for (const e of input.effects) {
        if (e.type !== 'score_change')
            continue;
        const { control, implementation: impl, coverage = 1, strength = 1, } = e.params;
        if (!control)
            continue;
        const v = typeof impl === 'number' ? clamp(impl * coverage * strength, 0, 1) : null;
        credits.set(control, [...(credits.get(control) ?? []), { effect: e.id, source: e.source, credit: v }]);
        if (v === null)
            continue;
        implementation.set(control, Math.min(implementation.get(control) ?? 1, v));
    }
    const controlIds = [...askedBy.keys()].sort(byKey);
    const inDomain = (d) => controlIds.filter((c) => input.controls[c].domain === d);
    // The industry profile's domain weights replace the model's (task 79).
    const domainWeight = (d) => input.profile?.domain_weights[d.code] ?? d.weight;
    const killSwitch = (c) => killSwitchCap(c, input.controls[c], input.profile);
    const domainsApplying = CONTROL_DOMAINS.filter((d) => inDomain(d.code).length);
    const domainWeightTotal = domainsApplying.reduce((s, d) => s + domainWeight(d), 0);
    const controlScores = [];
    const domains = [];
    for (const d of domainsApplying) {
        const ids = inDomain(d.code);
        const critTotal = ids.reduce((s, c) => s + input.controls[c].criticality, 0);
        const dw = domainWeight(d) / domainWeightTotal;
        let declared = 0;
        let confident = 0;
        let answeredHere = 0;
        for (const c of ids) {
            const def = input.controls[c];
            const share = def.criticality / critTotal;
            const qs = askedBy.get(c).filter(answered);
            if (qs.length)
                answeredHere++;
            const impl = qs.length ? (implementation.get(c) ?? null) : null;
            const best = qs
                .map((q) => {
                const declared = input.answers[q].provenance;
                const counted = checkedProvenance(declared, input.claims?.[q]);
                return { provenance: counted, checked: counted !== declared };
            })
                .sort((a, b) => EVIDENCE_CONFIDENCE[b.provenance] - EVIDENCE_CONFIDENCE[a.provenance] || Number(a.checked) - Number(b.checked))[0] ?? null; // prettier-ignore
            const provenance = best?.provenance ?? null;
            const confidence = provenance ? EVIDENCE_CONFIDENCE[provenance] : 0;
            declared += share * (impl ?? 0);
            confident += share * (impl ?? 0) * confidence;
            controlScores.push({
                control: c,
                domain: d.code,
                weight: dw * share,
                implementation: impl,
                provenance,
                confidence,
                kill_switch: killSwitch(c) !== null,
                ...(best?.checked ? { checked: true } : {}),
            });
        }
        const ec = declared ? confident / declared : 0;
        domains.push({
            domain: d.code,
            name: d.name,
            weight: dw,
            score: 100 * declared,
            effective: 100 * declared * (0.75 + 0.25 * ec),
            controls: ids.length,
            answered: answeredHere,
        });
    }
    const inherent = inherentRisk(input);
    const basis = {
        inherent: inherent.value,
        controls: controlScores.map((c) => ({
            control: c.control,
            weight: c.weight,
            implementation: c.implementation,
            confidence: c.confidence,
            cap: killSwitch(c.control)?.cap ?? null,
        })),
    };
    const layers = scoreBasis(basis);
    const { declared, evidence } = layers;
    const failed = [];
    const pending = [];
    for (const c of controlScores) {
        if (!c.kill_switch)
            continue;
        if (c.implementation === null)
            pending.push(c.control);
        else if (c.implementation === 0)
            failed.push({ control: c.control, name: input.controls[c.control].name, ...killSwitch(c.control) }); // prettier-ignore
    }
    failed.sort((a, b) => a.cap - b.cap || byKey(a.control, b.control));
    const { cap, score } = layers;
    const resilience = Object.fromEntries(Object.keys(THREATS).map((t) => {
        const ws = Object.entries(THREATS[t].domains);
        const present = ws.filter(([d]) => domains.some((x) => x.domain === d));
        const total = present.reduce((s, [, w]) => s + w, 0);
        const v = total
            ? present.reduce((s, [d, w]) => s + w * domains.find((x) => x.domain === d).effective, 0) / total
            : 0;
        return [t, round(v)];
    }));
    const provisional = Object.entries(input.questions).some(([k, q]) => q.mandatory && shown(k) && states[k].state !== 'waiting' && !answered(k));
    const result = {
        algorithm: CYBERSCORE_ALGORITHM,
        score,
        uncapped: layers.uncapped,
        band: bandOf(score),
        provisional,
        inherent: { ...inherent, value: round(inherent.value) },
        controls: {
            declared: round(declared),
            evidence_confidence: round(evidence, 3),
            effective: round(layers.effective),
            applicable: controlScores.length,
            answered: controlScores.filter((c) => askedBy.get(c.control).some(answered)).length,
        },
        residual_risk: round(layers.residual),
        domains: domains.map((d) => ({
            ...d,
            weight: round(d.weight, 4),
            score: round(d.score),
            effective: round(d.effective),
        })),
        control_scores: controlScores.map((c) => ({ ...c, weight: round(c.weight, 5) })),
        kill_switches: { failed, pending, cap },
        resilience,
        loss: lossModel(input, inherent.industry, resilience, evidence),
        ...(input.profile
            ? {
                profile: {
                    industry: inherent.industry,
                    name: input.profile.name,
                    minimum_band: input.profile.minimum_band ?? null,
                    below_minimum: belowMinimum(bandOf(score), input.profile.minimum_band),
                },
            }
            : {}),
    };
    return { result, basis, askedBy, answered, credits };
}
/** The controls a question's answers give credit to. */
function scoredControls(q) {
    const out = new Set();
    for (const e of [...q.effects, ...q.options.flatMap((o) => o.effects)])
        if (e.type === 'score_change' && typeof e.params.control === 'string')
            out.add(e.params.control);
    return out;
}
function answerOf(input, key) {
    return input.answers[key]?.value;
}
function revenueOf(input) {
    const v = answerOf(input, INHERENT_INPUTS.revenue);
    if (typeof v === 'number')
        return v;
    if (v && typeof v === 'object' && typeof v.amount === 'number')
        return v.amount;
    return null;
}
function staffOf(input) {
    const v = answerOf(input, INHERENT_INPUTS.staff);
    return typeof v === 'number' ? v : null;
}
const pointsFor = (table, v) => table.find(([below]) => v < below)[1];
/**
 * Layer 1. Points for size, data held and technology, times the industry multiplier, capped at 100.
 * Calibrated so the prototype's worked example (a RM 28m manufacturer with 96 staff and about 4,000 records,
 * machines on the network, its own servers, remote access, a website and Microsoft 365) comes to 64, and
 * checked against the prototype's twelve portfolio companies (fixtures/companies.ts). Unknown revenue and
 * staff count as the middle band; unknown records as 1,000–10,000. Records come from the `records` fact
 * until a question or the digital twin supplies them.
 */
function inherentRisk(input) {
    const industry = typeof input.facts.industry === 'string' ? input.facts.industry : null;
    const multiplier = input.profile?.multiplier ?? ((industry && INDUSTRY_MULTIPLIERS[industry]) || 1);
    const yes = (k) => answerOf(input, k) === 'yes';
    const revenue = revenueOf(input);
    const staff = staffOf(input);
    const email = answerOf(input, INHERENT_INPUTS.cloud_email);
    const factors = [
        { factor: 'base', points: 3 },
        revenue === null
            ? { factor: 'revenue', points: 12, assumed: true }
            : { factor: 'revenue', points: pointsFor(REVENUE_POINTS, revenue) },
        staff === null
            ? { factor: 'staff', points: 6, assumed: true }
            : { factor: 'staff', points: pointsFor(STAFF_POINTS, staff) },
        typeof input.facts.records === 'number'
            ? { factor: 'records', points: pointsFor(RECORD_POINTS, input.facts.records) }
            : { factor: 'records', points: 2, assumed: true },
        {
            factor: 'connected_machines',
            points: yes(INHERENT_INPUTS.ot) ? 8 : answerOf(input, INHERENT_INPUTS.ot) === 'not_sure' ? 4 : 0,
        },
        { factor: 'own_servers', points: yes(INHERENT_INPUTS.own_servers) ? 4 : 0 },
        { factor: 'remote_access', points: yes(INHERENT_INPUTS.remote_access) ? 5 : 0 },
        { factor: 'website', points: yes(INHERENT_INPUTS.website) ? 3 : 0 },
        { factor: 'cloud_iaas', points: yes(INHERENT_INPUTS.cloud_iaas) ? 3 : 0 },
        {
            factor: 'cloud_email',
            points: (Array.isArray(email) ? email : [email]).some((v) => v === 'microsoft_365' || v === 'google_workspace')
                ? 1
                : 0,
        },
    ];
    const raw = factors.reduce((s, f) => s + f.points, 0);
    return { value: Math.min(100, raw * multiplier), industry, multiplier, factors };
}
/**
 * Layer 4: probability × severity per threat. Resilience lowers frequency and downtime; low evidence
 * confidence widens the range. Reference figures until claims data calibrates them.
 */
function lossModel(input, industry, res, evidence) {
    const revenue = revenueOf(input);
    if (revenue === null)
        return null;
    const assumptions = [];
    const base = LOSS_BASE[(industry && industry in LOSS_BASE ? industry : 'other')];
    if (!industry || !(industry in LOSS_BASE))
        assumptions.push('Industry frequencies: SME average');
    const staff = staffOf(input);
    const records = typeof input.facts.records === 'number' ? input.facts.records : (staff ?? 50) * RECORDS_PER_STAFF;
    if (typeof input.facts.records !== 'number')
        assumptions.push(`Records held estimated at ${RECORDS_PER_STAFF} per member of staff (${records.toLocaleString('en-MY')})`); // prettier-ignore
    const day = revenue / 300; // a working day's revenue
    const freq = (r) => 1.6 - (r / 100) * 1.2;
    const downDays = (r) => Math.max(2, 22 - (r / 100) * 17);
    const severity = {
        ransomware: day * downDays(res.ransomware) * 0.55 * base.downtime + 180_000 + revenue * 0.004,
        breach: records * base.per_record + 220_000 + revenue * 0.006,
        bec: 90_000 + revenue * 0.0045,
        cloud: day * downDays(res.cloud) * 0.22 + 60_000,
        third: day * downDays(res.third) * 0.3 + 120_000,
    };
    const width = 0.2 + (1 - evidence) * 0.5;
    const scenarios = Object.keys(THREATS).map((t) => {
        const p = base[t] * freq(res[t]);
        const s = severity[t];
        return {
            threat: t,
            name: THREATS[t].name,
            probability: round(p, 4),
            severity: Math.round(s),
            low: Math.round(s * (1 - width * 0.8)),
            high: Math.round(s * (1 + width * 1.4)),
            expected: Math.round(p * s),
        };
    });
    const eal = scenarios.reduce((n, s) => n + base[s.threat] * freq(res[s.threat]) * severity[s.threat], 0);
    return {
        currency: 'MYR',
        scenarios,
        expected_annual: Math.round(eal),
        expected_annual_low: Math.round(eal * (1 - width)),
        expected_annual_high: Math.round(eal * (1 + width * 1.3)),
        probable_maximum: Math.round(Math.max(...scenarios.map((s) => s.high)) * 1.15),
        assumptions,
    };
}
/**
 * The questions a journey won't ask, for `out_of_scope`: those outside it, those in a skipped stage, and
 * those in a locked stage whose entry condition already fails (it will be skipped once it is reached).
 * Questions a route counts as not in place stay in, even when the route skipped their whole stage.
 */
export function journeyOutOfScope(journey, questions, stages, ctx) {
    if (!journey)
        return [];
    const states = new Map(journey.stages.map((s) => [s.key, s]));
    const display = displayStates(questions, ctx);
    const answers = {};
    for (const [k, v] of Object.entries(ctx.answers))
        if (display[k]?.state !== 'hidden')
            answers[k] = v;
    for (const [k, d] of Object.entries(display))
        if (d.state === 'implied')
            answers[k] = d.value;
    const fails = (key) => {
        const s = states.get(key);
        const entry = stages[key]?.entry;
        return (s.state === 'skipped' ||
            (s.state === 'locked' &&
                !!entry &&
                !s.entry_waiting_on.length &&
                !evaluate(entry, { answers, facts: ctx.facts })));
    };
    return Object.keys(questions)
        .filter((k) => {
        const at = journey.questions[k];
        // Routes that skip a whole stage still count its questions as not in place (task 85).
        if (at && display[k]?.state === 'implied' && states.get(at.stage).routed_by)
            return false;
        return !at || fails(at.stage);
    })
        .sort(byKey);
}
//# sourceMappingURL=scoring.js.map